import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Compass, LayoutDashboard, Plus, LogOut, User as UserIcon, CreditCard, Box, Sparkles, Shield, Download, WifiOff, Wrench, MessagesSquare, BookOpen, HardHat } from "lucide-react";
import { usePWAInstall, useOnline } from "@/lib/pwa";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { canInstall, isStandalone, promptInstall } = usePWAInstall();
  const online = useOnline();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavLink = ({ to, icon: Icon, label, testid }) => (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-2 px-3 py-2 text-sm font-mono2 uppercase tracking-widest transition-colors ${
        pathname === to ? "text-primary" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-4 w-4" />
      <span className="hidden md:inline">{label}</span>
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8">
        <Link to={user ? "/dashboard" : "/"} data-testid="nav-logo" className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Costobuild" className="h-9 w-9" />
          <span className="font-heading text-lg font-extrabold uppercase tracking-tight">Costobuild</span>
        </Link>

        <div className="flex items-center gap-1 sm:gap-2">
          {!online && (
            <span data-testid="offline-indicator" className="mr-1 flex items-center gap-1 font-mono2 text-[10px] uppercase tracking-widest text-amber-400">
              <WifiOff className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Offline</span>
            </span>
          )}
          {canInstall && !isStandalone && (
            <Button onClick={promptInstall} size="sm" variant="outline" data-testid="install-app-btn"
              className="mr-1 rounded-sm border-primary/60 font-mono2 text-xs uppercase tracking-widest text-primary hover:bg-primary/10">
              <Download className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Install</span>
            </Button>
          )}
          {user ? (
            <>
              <NavLink to="/dashboard" icon={LayoutDashboard} label="Projects" testid="nav-dashboard" />
              <NavLink to="/builder" icon={Plus} label="New Build" testid="nav-builder" />
              <NavLink to="/generate" icon={Box} label="3D Generator" testid="nav-generate" />
              <NavLink to="/forum" icon={MessagesSquare} label="Forum" testid="nav-forum" />
              <NavLink to="/contractors" icon={HardHat} label="Pros" testid="nav-contractors" />
              <NavLink to="/blog" icon={BookOpen} label="Blog" testid="nav-blog" />
              <NavLink to="/billing" icon={CreditCard} label="Billing" testid="nav-billing" />
              <NavLink to="/profile" icon={UserIcon} label="Profile" testid="nav-profile" />
              {user.role === "admin" && <NavLink to="/admin" icon={Shield} label="Admin" testid="nav-admin" />}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="nav-logout"
                className="ml-1 text-muted-foreground hover:text-destructive"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <div className="mr-2 hidden items-center lg:flex">
                <NavLink to="/generate" icon={Box} label="3D Generator" testid="nav-generate" />
                <NavLink to="/home-improvement" icon={Wrench} label="Reno Costs" testid="nav-home-improvement" />
                <NavLink to="/forum" icon={MessagesSquare} label="Forum" testid="nav-forum" />
                <NavLink to="/contractors" icon={HardHat} label="Pros" testid="nav-contractors" />
                <NavLink to="/blog" icon={BookOpen} label="Blog" testid="nav-blog" />
                <NavLink to="/pricing" icon={CreditCard} label="Pricing" testid="nav-pricing" />
              </div>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" size="sm" className="font-mono2 uppercase tracking-widest text-xs">
                  Sign In
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button size="sm" className="rounded-sm bg-primary font-mono2 text-xs uppercase tracking-widest text-black hover:bg-primary/90">
                  Get Started
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
